// Status is confirmed by the Product API. Pricing is previewed and saved through
// the shared Sales Simulation engine; no financial formula lives in this view.
import React, { useEffect, useRef, useState } from "react";
import { FaChartLine, FaCheck, FaCircleNotch, FaExclamationTriangle, FaSlidersH } from "react-icons/fa";
import { elakayAutoText as text } from "../../pageInside/dataElakay";
import PricingModeSelector from "../../pageInside/PricingModeSelector";
import SimulationTaxInput from "../../pageInside/SimulationTaxInput";
import { setProductSellActive, previewProductProfitability, saveProductPricing } from "../../auth/apiProduct";
import "./ProductStatusSwitch.css";

const usd = (value) => Number.isFinite(Number(value))
  ? Number(value).toLocaleString("en-US", { style: "currency", currency: "USD" }) : "—";
const INPUT_KEYS = ["sellerShippingCost", "otherCosts", "buyerShipping", "taxRate"];
const REASON_KEY = {
  NOT_PROFITABLE: ["h_ps_not_profitable", "Product Not Profitable"],
  DESIRED_PROFIT_NOT_REACHED: ["h_ps_desired_not_reached", "Desired Profit Not Reached"],
  MISSING_DATA: ["h_ps_missing_data", "Some financial information is missing. Add your cost price to verify your profit."],
  PROVIDER_UNAVAILABLE: ["h_ps_provider_unavailable", "The Provider product is no longer available, so this product cannot be activated."],
  PROVIDER_DELETED: ["h_ptr_unavailable_deleted", "Product unavailable — Deleted by Provider"],
};
const MISSING_KEY = {
  name: ["h_ps_field_name", "Product name"], description: ["h_ps_field_description", "Product description"],
  categories: ["h_ps_field_categories", "Categories"], images: ["h_ps_field_images", "Images"],
  filters: ["h_ps_field_filters", "Required filters / variants"], variants: ["h_ps_field_filters", "Required filters / variants"],
  stock: ["h_ps_field_inventory", "Inventory / stock"], inventory: ["h_ps_field_inventory", "Inventory / stock"],
  shopAddress: ["h_ps_field_address", "Shop address"], shipping: ["h_ps_field_shipping", "Shipping information"],
  fulfillment: ["h_ps_field_fulfillment", "Fulfillment"], price: ["h_ps_selling_price", "Selling Price"],
  provider: ["h_ps_provider_unavailable", "The Provider product is no longer available, so this product cannot be activated."],
};
const reasonText = (code) => REASON_KEY[code] ? text(...REASON_KEY[code]) : text("h_ps_cannot_activate", "Cannot Activate Product");
const missingText = (code) => MISSING_KEY[code] ? text(...MISSING_KEY[code]) : text("h_ps_required_information", "Required product information");
const initialDraft = (product) => ({
  pricingMode: product?.pricingTarget?.mode === "desired_profit" ? "desired_profit" : "selling_price",
  sellingPrice: String(product?.price ?? ""),
  desiredProfit: String(product?.pricingTarget?.desiredProfit ?? ""),
  simulationInputs: Object.fromEntries(INPUT_KEYS.map((key) => [key, String(product?.pricingTarget?.simulationInputs?.[key] ?? 0)])),
});
const payloadFor = (draft) => ({
  pricingMode: draft.pricingMode,
  ...(draft.pricingMode === "desired_profit" ? { desiredProfit: Number(draft.desiredProfit) } : { sellingPrice: Number(draft.sellingPrice) }),
  simulationInputs: Object.fromEntries(INPUT_KEYS.map((key) => [key, Number(draft.simulationInputs[key]) || 0])),
});
const draftKey = (draft) => JSON.stringify(payloadFor(draft));

function MoneyInput({ label, value, onChange, disabled, min = "0", suffix }) {
  return <label className="ps-field">
    <span>{label}</span>
    <span className="ps-money-input"><span aria-hidden="true">{suffix || "$"}</span>
      <input type="number" inputMode="decimal" min={min} step="0.01" value={value} disabled={disabled}
        onChange={(event) => onChange(event.target.value)} />
    </span>
  </label>;
}

export default function ProductStatusSwitch({ productId, token, product, onChanged }) {
  const external = ["dropshipping", "printify"].includes(product?.sourceType);
  const providerSource = product?.sourceType === "provider_internal";
  const [sell, setSell] = useState(product?.sellActive?.sell === true);
  const [draft, setDraft] = useState(() => initialDraft(product));
  const [savedKey, setSavedKey] = useState(() => draftKey(initialDraft(product)));
  const [preview, setPreview] = useState(null);
  const [pending, setPending] = useState(true);
  const [busy, setBusy] = useState("");
  const [problem, setProblem] = useState(null);
  const [saved, setSaved] = useState(false);
  const [refresh, setRefresh] = useState(0);
  const requestVersion = useRef(0);
  const mutationVersion = useRef(0);
  const persistedKey = JSON.stringify(initialDraft(product));
  const inputKey = draftKey(draft);
  const dirty = inputKey !== savedKey;
  const validInput = (draft.pricingMode === "desired_profit" ? Number(draft.desiredProfit) > 0 : Number(draft.sellingPrice) > 0)
    && INPUT_KEYS.every((key) => Number.isFinite(Number(draft.simulationInputs[key])) && Number(draft.simulationInputs[key]) >= 0)
    && Number(draft.simulationInputs.taxRate) <= 1;

  useEffect(() => {
    setSell(product?.sellActive?.sell === true);
  }, [productId, product?.sellActive?.sell]);
  useEffect(() => {
    const restored = initialDraft(product);
    setDraft(restored);
    setSavedKey(draftKey(restored));
    setSaved(false);
    setProblem(null);
    // Only persisted pricing changes reset an in-progress pricing draft.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productId, persistedKey]);
  useEffect(() => () => { mutationVersion.current += 1; }, [productId]);

  useEffect(() => {
    const version = ++requestVersion.current;
    let cancelled = false;
    setPreview(null);
    if (!productId || external || !validInput) { setPending(false); return undefined; }
    setPending(true);
    const timer = setTimeout(async () => {
      try {
        const result = await previewProductProfitability(token, productId, payloadFor(draft));
        if (cancelled || requestVersion.current !== version) return;
        if (!result || result.error || result.success === false) {
          setProblem({ error: text("h_ps_preview_failed", "The simulation could not be loaded. Try again.") });
          return;
        }
        setPreview(result);
      } catch {
        if (!cancelled && requestVersion.current === version) setProblem({ error: text("h_ps_preview_failed", "The simulation could not be loaded. Try again.") });
      } finally {
        if (!cancelled && requestVersion.current === version) setPending(false);
      }
    }, 250);
    return () => { cancelled = true; clearTimeout(timer); };
    // inputKey tracks the normalized API payload, including every configurable cost.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productId, token, external, inputKey, validInput, refresh, product]);

  if (external) return null;

  const changeDraft = (key, value) => {
    requestVersion.current += 1;
    setPreview(null);
    setPending(true);
    setSaved(false);
    setProblem(null);
    setDraft((current) => ({ ...current, [key]: value }));
  };
  const changeCost = (key, value) => changeDraft("simulationInputs", { ...draft.simulationInputs, [key]: value });
  const report = preview?.report;
  const profitable = Boolean(report?.complete && (report.activation?.ok ?? preview?.ok));
  const eligible = Boolean(preview && (preview.canActivate ?? preview.ok));
  const missing = problem?.missing || preview?.missing || [];
  const reasons = problem?.reasons || preview?.reasons || [];
  const toggleBlocked = Boolean(busy) || (!sell && (pending || dirty || !eligible));

  const toggle = async () => {
    if (toggleBlocked) return;
    const version = ++mutationVersion.current;
    setBusy("status"); setProblem(null); setSaved(false);
    try {
      const result = await setProductSellActive(token, productId, { sell: !sell });
      if (version !== mutationVersion.current) return;
      if (!result || result.error || result.success === false) { setProblem(result || {}); return; }
      const confirmed = result.product?.sellActive?.sell ?? result.sell;
      if (typeof confirmed !== "boolean") { setProblem({ error: text("h_ps_save_failed", "Your changes could not be saved. Try again.") }); return; }
      setSell(confirmed);
      onChanged?.(result.product || { sellActive: { ...product?.sellActive, sell: confirmed, reason: confirmed ? null : "SELLER_DEACTIVATED" } });
      setRefresh((value) => value + 1);
    } catch {
      if (version === mutationVersion.current) setProblem({ error: text("h_ps_save_failed", "Your changes could not be saved. Try again.") });
    } finally {
      if (version === mutationVersion.current) setBusy("");
    }
  };

  const savePricing = async () => {
    if (!validInput || !report?.complete || pending || busy || !dirty) return;
    const version = ++mutationVersion.current;
    setBusy("pricing"); setProblem(null); setSaved(false);
    try {
      const result = await saveProductPricing(token, productId, payloadFor(draft));
      if (version !== mutationVersion.current) return;
      if (!result || result.error || result.success === false) { setProblem(result || {}); return; }
      const updated = result.product;
      if (!updated) { setProblem({ error: text("h_ps_save_failed", "Your changes could not be saved. Try again.") }); return; }
      const restored = initialDraft(updated);
      setDraft(restored); setSavedKey(draftKey(restored));
      setSell(updated.sellActive?.sell === true);
      onChanged?.(updated);
      setSaved(true);
      setRefresh((value) => value + 1);
    } catch {
      if (version === mutationVersion.current) setProblem({ error: text("h_ps_save_failed", "Your changes could not be saved. Try again.") });
    } finally {
      if (version === mutationVersion.current) setBusy("");
    }
  };

  return <section className="content-card product-status-pricing" data-testid="product-status" aria-busy={Boolean(busy)}>
    <header className="ps-section ps-status-header">
      <h3>{text("h_ps_title", "Product Status")}</h3>
      <div className="ps-status-control">
        <span className={!sell ? "ps-status-current" : ""}>{text("h_ps_inactive", "Inactive")}</span>
        <button type="button" role="switch" aria-checked={sell} disabled={toggleBlocked} onClick={toggle}
          className={`ps-switch${sell ? " ps-switch--on" : ""}`}
          aria-label={sell ? text("h_ps_deactivate", "Deactivate Product") : text("h_ps_activate", "Activate Product")}>
          <span className="ps-switch-thumb">{busy === "status" && <FaCircleNotch className="ps-spinner" aria-hidden="true" />}</span>
        </button>
        <span className={sell ? "ps-status-current ps-status-active" : ""}>{sell && <FaCheck data-testid="ps-active-check" aria-hidden="true" />} {text("h_ps_active", "Active")}</span>
      </div>
      {(product?.providerLinkData?.providerDeletedAt || product?.sellActive?.reason === "PROVIDER_DELETED") && <div className="ps-notice ps-notice--warning" role="status" data-testid="ps-provider-deleted">
        <strong>{text("h_ptr_unavailable_deleted", "Product unavailable — Deleted by Provider")}</strong>
        <p>{text("h_ptr_unavailable_deleted_note", "The Provider deleted this product. It cannot be sold until the Provider restores it. Your settings and history are kept.")}</p>
      </div>}
      {!sell && product?.sellActive?.reason && !["SELLER_DEACTIVATED", "PROVIDER_DELETED"].includes(product.sellActive.reason) && <div className="ps-notice ps-notice--warning" role="status">
        <strong>{text("h_ps_auto_deactivated", "Product automatically deactivated")}</strong>
        <p>{providerSource ? text("h_ps_provider_price_changed", "Provider pricing or availability changed. Adjust your pricing before reactivating this product.") : text("h_ps_auto_off", "This product was switched off automatically because it is no longer profitable.")}</p>
      </div>}
      {!sell && dirty && <p className="ps-hint">{text("h_ps_save_before_activate", "Save your pricing changes before activating this product.")}</p>}
    </header>

    <div className="ps-section">
      <h3><FaSlidersH aria-hidden="true" />{text("h_ps_pricing_title", "Pricing & Target Profit")}</h3>
      <PricingModeSelector value={draft.pricingMode} onChange={(value) => changeDraft("pricingMode", value)} disabled={Boolean(busy)} />
      <div className="ps-pricing-inputs">
        <MoneyInput label={draft.pricingMode === "desired_profit" ? text("h_ps_desired_profit", "Desired Profit") : text("h_ps_target_price", "Target Selling Price")}
          value={draft.pricingMode === "desired_profit" ? draft.desiredProfit : draft.sellingPrice} min="0.01" disabled={Boolean(busy)}
          onChange={(value) => changeDraft(draft.pricingMode === "desired_profit" ? "desiredProfit" : "sellingPrice", value)} />
        {providerSource && <label className="ps-field"><span>{text("h_ps_provider_price", "Provider Price")}</span>
          <input className="ps-readonly" value={report?.complete ? usd(report.productCost) : "—"} readOnly />
          <small>{text("h_ps_provider_readonly", "Read only — managed by the Provider")}</small></label>}
      </div>
      <details className="ps-cost-settings">
        <summary>{text("h_ps_cost_settings", "Shipping & Applicable Costs")}</summary>
        <p className="ps-hint">{text("h_ps_cost_settings_hint", "Use the costs and buyer charges for this sale. Provider fulfillment is included automatically when applicable.")}</p>
        <div className="ps-cost-inputs">
          <MoneyInput label={text("h_ps_seller_shipping", "Shipping paid by Seller")} value={draft.simulationInputs.sellerShippingCost} onChange={(value) => changeCost("sellerShippingCost", value)} disabled={Boolean(busy)} />
          <MoneyInput label={text("h_ps_other_costs", "Other applicable costs")} value={draft.simulationInputs.otherCosts} onChange={(value) => changeCost("otherCosts", value)} disabled={Boolean(busy)} />
          <MoneyInput label={text("h_ps_buyer_shipping", "Shipping paid by Buyer")} value={draft.simulationInputs.buyerShipping} onChange={(value) => changeCost("buyerShipping", value)} disabled={Boolean(busy)} />
          <SimulationTaxInput value={draft.simulationInputs.taxRate} onChange={(value) => changeCost("taxRate", String(value))} disabled={Boolean(busy)} />
        </div>
      </details>
    </div>

    <div className="ps-section ps-simulation" aria-live="polite">
      <h3><FaChartLine aria-hidden="true" />{text("h_ps_simulation", "Sales Simulation")}</h3>
      {pending && <p className="ps-hint"><FaCircleNotch className="ps-spinner" aria-hidden="true" /> {text("h_ps_calculating", "Calculating…")}</p>}
      {!validInput && <p className="ps-hint">{text("h_ps_valid_price", "Enter a positive selling price or desired profit and valid costs.")}</p>}
      {preview?.costUnknown && <p className="ps-notice ps-notice--warning">{text("h_ps_cost_unknown", "Your cost price is not set, so profit cannot be verified.")}</p>}
      {report?.complete && <>
        <dl className="ps-breakdown">
          <div><dt>{providerSource ? text("h_ps_provider_price", "Provider Price") : text("h_ps_purchase_price", "Purchase Price")}</dt><dd>{usd(report.productCost)}</dd></div>
          <div><dt>{text("h_ps_payment_fees", "Payment processing")}</dt><dd>{usd(report.paymentFees)}</dd></div>
          <div><dt>{text("h_ps_elakay_fees", "Elakay fees")}</dt><dd>{usd(report.elakayFees)}</dd></div>
          {Number(report.elakayFees) > 0 && <div className="ps-fee-breakdown-row">
            <details>
              <summary>{text("h_ps_elakay_fees_breakdown", "Breakdown")}</summary>
              <div className="ps-fee-breakdown">
                {Number(report.serviceFee) > 0 && <div><dt>{text("h_ps_service_fee", "Service Fee")}</dt><dd>{usd(report.serviceFee)}</dd></div>}
                {Number(report.saleFee) > 0 && <div><dt>{text("h_ps_selling_fee", "Selling Fee")}</dt><dd>{usd(report.saleFee)}</dd></div>}
                {Number(report.referralFee) > 0 && <div><dt>{text("h_ps_referral_fee", "Referral Fee")}</dt><dd>{usd(report.referralFee)}</dd></div>}
                {Number(report.fixedFee) > 0 && <div><dt>{text("h_ps_closing_fee", "Closing Fee")}</dt><dd>{usd(report.fixedFee)}</dd></div>}
              </div>
            </details>
          </div>}
          <div><dt>{text("h_ps_fulfillment", "Provider fulfillment / logistics")}</dt><dd>{usd(report.logisticsCost)}</dd></div>
          <div><dt>{text("h_ps_seller_shipping", "Shipping paid by Seller")}</dt><dd>{usd(report.sellerShippingCost)}</dd></div>
          <div><dt>{text("h_ps_other_costs", "Other applicable costs")}</dt><dd>{usd(report.otherCosts)}</dd></div>
          <div><dt>{text("h_sim_tax_amount", "Applicable sales tax")}</dt><dd>{usd(report.taxAmount ?? 0)}</dd></div>
          <div className="ps-breakdown-total"><dt>{text("h_ps_total_costs", "Estimated Costs")}</dt><dd>{usd(report.totalCosts)}</dd></div>
          <div><dt>{draft.pricingMode === "desired_profit" ? text("h_ps_calculated_price", "Calculated Selling Price") : text("h_ps_selling_price", "Selling Price")}</dt><dd data-testid="ps-selling-price">{usd(report.sellingPrice)}</dd></div>
          {Number(report.discountPercent) > 0 && <>
            <div><dt>{text("h_ps_discount", "Discount")} ({report.discountPercent}%)</dt><dd>−{usd(report.discountAmount)}</dd></div>
            <div><dt>{text("h_ps_discounted_price", "Price after discount")}</dt><dd>{usd(report.effectiveSellingPrice)}</dd></div>
          </>}
          <div className={`ps-breakdown-profit ${profitable ? "ps-positive" : "ps-negative"}`}><dt>{text("h_ps_net_profit", "Net Profit")}</dt><dd data-testid="ps-net">{usd(report.netProfit)}</dd></div>
          <div><dt>{text("h_ps_profit_margin", "Profit Margin")}</dt><dd>{report.marginPercent}%</dd></div>
        </dl>
        <p className={`ps-profit-state ${profitable ? "ps-positive" : "ps-negative"}`} data-testid="ps-profit-state">
          {profitable ? <FaCheck aria-hidden="true" /> : <FaExclamationTriangle aria-hidden="true" />}
          {profitable ? (draft.pricingMode === "desired_profit" ? text("h_ps_target_reached", "Profit Target Reached") : text("h_ps_profitable", "Product Profitable"))
            : reasonText(report.activation?.reasons?.[0] || reasons[0] || "NOT_PROFITABLE")}
        </p>
        {!profitable && <p className="ps-hint">{text("h_ps_adjust_price", "Increase your selling price or use Desired Profit to calculate a profitable price.")}</p>}
        <p className="ps-hint">{text("h_ps_simulation_assumption", "Estimate for one item per order. Buyer shipping and sales tax affect payment fees; sales tax is passed through.")}</p>
      </>}
      {(problem || (!pending && !sell && (missing.length > 0 || reasons.length > 0))) && <div className="ps-notice ps-notice--warning" role="alert" data-testid="ps-problem">
        <strong>{text("h_ps_cannot_activate", "Cannot Activate Product")}</strong>
        {problem?.error && !reasons.length && !missing.length && <p>{problem.error}</p>}
        {reasons.length > 0 && <ul>{reasons.map((reason) => <li key={reason}>{reasonText(reason)}</li>)}</ul>}
        {missing.length > 0 && <><p>{text("h_ps_incomplete", "Complete the required product information before activating it:")}</p><ul>{missing.map((field) => <li key={field}>{missingText(field)}</li>)}</ul></>}
        {!preview && !pending && validInput && <button type="button" className="ps-retry" disabled={Boolean(busy)} onClick={() => { setProblem(null); setRefresh((value) => value + 1); }}>{text("h_ps_retry", "Try again")}</button>}
      </div>}
      <div className="ps-save-row">
        <button type="button" className="ps-save-button" disabled={Boolean(busy) || pending || !validInput || !report?.complete || !dirty} onClick={savePricing}>
          {busy === "pricing" && <FaCircleNotch className="ps-spinner" aria-hidden="true" />}
          {draft.pricingMode === "desired_profit" ? text("h_ps_apply_price", "Apply Calculated Price") : text("h_ps_save_pricing", "Save Pricing")}
        </button>
        <span className="ps-hint">{text("h_ps_save_separate", "Saving pricing does not activate your product.")}</span>
      </div>
      {saved && <p className="ps-positive" role="status">{text("h_ps_pricing_saved", "Pricing saved")}</p>}
    </div>
  </section>;
}
